import { useCallback, useRef } from 'react'
import type { Variants } from 'framer-motion'
import { motion, useAnimation } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  isDashboardBlocked,
  useFeatureFlag
} from '@/application/use-feature-flag'
import { BlockedDashboardOverlay } from '@/ui/components/overlays/blocked-dashboard-overlay'
import { ProcessesFilters } from '@/ui/components/processes/processes-filters'
import { ProcessesTabs } from '@/ui/components/processes/processes-tabs'
import { ExportProcessesButton } from '@/ui/components/processes/export-processes-button'
import { NewReturnButton } from '@/ui/components/processes/new-return-button'
import { useAppSelector } from '@/redux/hooks'

export const OrdersPage = () => {
  const { t } = useTranslation()
  const { isFlagEnabled } = useFeatureFlag(isDashboardBlocked, {
    pageName: 'orders'
  })

  const currentEcommerce = useAppSelector(
    (store) => store.generalData.selectedEcommerce
  )

  return (
    <div
      className="relative h-full [&:has(#portal)]:overflow-hidden"
      id="orders-page"
    >
      {isFlagEnabled && <BlockedDashboardOverlay />}
      <div className="min-h-full bg-neutral-100/10 p-4">
        <div className="flex items-center justify-between gap-3.5 border-b border-solid border-neutral-200 pb-4">
          <TruckIcon />
          <header className="mr-auto space-y-1">
            <h1 className="font-[Roobert] text-lg font-medium leading-6 text-neutral-900">
              {t('orders.title')}
            </h1>
            <h2 className="font-[Roobert] text-sm font-normal leading-5 text-neutral-600">
              {t('orders.subtitle')}
            </h2>
          </header>
          <div className="flex items-center gap-4">
            {currentEcommerce && <NewReturnButton />}
            <ExportProcessesButton />
          </div>
        </div>
        <ProcessesFilters />
        <ProcessesTabs />
      </div>
    </div>
  )
}

export default OrdersPage

const truckVariants: Variants = {
  animate: {
    x: [0, 100, 100, -100, 0],
    opacity: [1, 1, 0, 0, 1],
    transition: {
      duration: 1,
      ease: 'easeInOut',
      times: [0, 0.5, 0.5, 0.5, 1]
    }
  }
}

const TruckIcon = () => {
  const controls = useAnimation()
  const isControlledRef = useRef(false)

  const handleMouseEnter = useCallback(() => {
    if (!isControlledRef.current) {
      controls.start('animate')
    }
  }, [controls])

  const handleMouseLeave = useCallback(() => {
    if (!isControlledRef.current) {
      controls.start('normal')
    }
  }, [controls])

  return (
    <div
      className="flex select-none items-center justify-center overflow-hidden rounded-full border border-solid border-neutral-200 bg-white p-3 shadow-sm transition-colors duration-200"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={22}
        height={22}
        variants={truckVariants}
        animate={controls}
        transition={{ duration: 0.2 }}
        stroke="currentColor"
        fill="none"
        strokeWidth="1.75"
        viewBox="0 0 24 24"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-neutral-700"
        aria-hidden
      >
        <path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"></path>
        <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"></path>
        <path d="M5 17h-2v-4m-1 -8h11v12m-4 0h6m4 0h2v-6h-8m0 -5h5l3 5"></path>
        <path d="M3 9l4 0"></path>
      </motion.svg>
    </div>
  )
}
